import clsx from "clsx";
import { useState } from "react";

import { LOGOS } from "../logos";
import { FunnelScreen, PrimaryButton } from "../screen";

import type { ScreenProps } from "../types";

// Twenty logos, four across. The last question before the account card, and the
// one whose answer we act on directly: it is the queue we build importers from,
// so what a lead ticks here decides what we can read for them and when.
//
// Nothing picked shows the way out instead of a Continue. One affordance at a
// time: the button that means "I'm done choosing" and the line that means "none
// of this is me" are answers to different questions.
export default function StackStep({
  step,
  answer,
  busy,
  save,
  skip,
  back,
}: ScreenProps) {
  const [picked, setPicked] = useState<string[]>(() => [
    ...(answer?.values ?? []),
  ]);

  const toggle = (id: string) => {
    if (busy) return;
    setPicked((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id]
    );
  };

  return (
    <FunnelScreen back={back} title={step.title} subtitle={step.subtitle} wide>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(step.options ?? []).map((option) => {
          const selected = picked.includes(option.id);

          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={selected}
              onClick={() => toggle(option.id)}
              className={clsx(
                "flex items-center gap-2.5 rounded-xl border p-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2",
                selected
                  ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600"
                  : "border-zinc-300 bg-white hover:border-zinc-400 hover:bg-zinc-50"
              )}
            >
              {/* The brand's mark. It says what the name beside it says, so a
                  screen reader hears the name once and not the picture. */}
              {LOGOS[option.id] ? (
                <img
                  src={LOGOS[option.id]}
                  alt=""
                  className="block h-9 w-9 shrink-0 rounded-md"
                />
              ) : (
                <span
                  aria-hidden
                  className={clsx(
                    "block h-9 w-9 shrink-0 rounded-md",
                    selected ? "bg-blue-100" : "bg-zinc-100"
                  )}
                />
              )}
              <span
                className={clsx(
                  "min-w-0 text-body font-medium leading-tight",
                  selected ? "text-zinc-900" : "text-zinc-700"
                )}
              >
                {option.name}
              </span>
            </button>
          );
        })}
      </div>

      {picked.length > 0 ? (
        <div className="mt-6">
          <PrimaryButton
            disabled={busy}
            onClick={() => save({ values: picked })}
          >
            Continue
          </PrimaryButton>
        </div>
      ) : (
        skip && (
          <p className="mt-6 text-center">
            <button
              type="button"
              onClick={() => !busy && skip()}
              className="text-body text-zinc-500 underline decoration-zinc-300 underline-offset-4 transition-colors hover:text-zinc-700 hover:decoration-zinc-500"
            >
              None of these
            </button>
          </p>
        )
      )}
    </FunnelScreen>
  );
}
